import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#050505] px-4">
      <div className="absolute h-[600px] w-[600px] rounded-full blur-[140px]"
        style={{ background: "radial-gradient(circle, rgba(212,175,55,0.10), transparent 70%)" }} />

      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="relative w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <img src="/deluxe-logo-main.jpeg" alt="DELUXE" className="w-40" />
          <span className="mt-2 font-sans text-[10px] uppercase tracking-[0.5em] text-zinc-500">
            Trading Analytics
          </span>
        </div>

        <div className="dlx-card dlx-grain p-7 sm:p-9">
          <h1 className="font-serif text-2xl text-white">Accesso Riservato</h1>
          <p className="mt-1 font-sans text-xs text-zinc-500">Login DELUXE — separato dalle credenziali MT5.</p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <div>
              <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Email</label>
              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#141418] px-3 focus-within:border-gold/40">
                <Mail size={15} className="text-zinc-500" />
                <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email" placeholder="nome@deluxe.trading"
                  className="w-full bg-transparent py-3 font-sans text-sm text-white outline-none placeholder:text-zinc-600" />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block font-sans text-[11px] uppercase tracking-widest text-zinc-500">Password</label>
              <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-[#141418] px-3 focus-within:border-gold/40">
                <Lock size={15} className="text-zinc-500" />
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password" placeholder="••••••••"
                  className="w-full bg-transparent py-3 font-sans text-sm text-white outline-none placeholder:text-zinc-600" />
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="rounded-lg border border-loss/30 bg-loss/10 px-3 py-2 font-sans text-xs text-loss">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="group flex w-full items-center justify-center gap-2 rounded-lg py-3 font-sans text-sm font-semibold text-black transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-60"
              style={{ background: "linear-gradient(135deg,#BF953F,#FCF6BA 45%,#AA771C)" }}>
              {loading ? "Accesso…" : "Accedi"}
              {!loading && <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>
        </div>
        <div className="mt-6 text-center font-mono text-[10px] tracking-widest text-zinc-600">
          Niccolò Picco × Diego Leccioli
        </div>
      </motion.div>
    </div>
  );
}
